<script setup lang="ts">
import type { HTMLAttributes } from "vue";
import { reactiveOmit } from "@vueuse/core";
import { X } from "lucide-vue-next";
import {
  DialogClose,
  DialogContent,
  type DialogContentEmits,
  type DialogContentProps,
  DialogOverlay,
  DialogPortal,
  useForwardPropsEmits,
} from "reka-ui";
import { cn } from "@/lib/utils";

const props = defineProps<
  DialogContentProps & {
    class?: HTMLAttributes["class"];
    overlayClass?: HTMLAttributes["class"];
    allowClose?: boolean;
  }
>();
const emits = defineEmits<DialogContentEmits>();

const delegatedProps = reactiveOmit(
  props,
  "class",
  "overlayClass",
  "allowClose"
);

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const screenshotStore = useScreenshotStore();
const { isCapturingScreenshot } = storeToRefs(screenshotStore);

const styles: any = {};
const style = cn(
  "fixed left-1/2 top-1/2 z-[1000000] grid max-w-[90%] min-w-[400px] overflow-auto -translate-x-1/2 -translate-y-1/2 gap-4 border bg-surface p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
  props.class
);
const overlayStyle = cn(
  "fixed inset-0 z-[9999] bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  props.overlayClass
);

styles[style] = true;
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      :class="[
        overlayStyle,
        {
          invisible: isCapturingScreenshot,
        },
      ]"
    />
    <DialogContent
      v-bind="forwarded"
      :class="{
        ...styles,
        invisible: isCapturingScreenshot,
      }"
    >
      <slot />

      <DialogClose
        v-if="allowClose"
        class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
      >
        <X class="w-4 h-4" />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
